import { createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "@/src/services/authService";
import { mapLoginResponse, mapUserFromAuthData, mapVerifyEmailResponse } from "@/src/services/authMappers";
import type {
  AppleAuthPayload,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  GoogleAuthPayload,
  LoginPayload,
  RefreshTokenPayload,
  RequestLoginOtpPayload,
  ResendVerificationPayload,
  ResetPasswordPayload,
  SignupPayload,
  UpdateProfilePayload,
  VerifyEmailPayload,
} from "@/src/types/auth.types";
import type { RootState } from "@/src/redux/types";
import { getApiErrorMessage } from "@/src/utils/helpers";

export const signupUser = createAsyncThunk(
  "auth/signup",
  async (payload: SignupPayload, { rejectWithValue }) => {
    try {
      return await authService.signup(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

/** @deprecated Password login — prefer requestLoginOtpUser */
export const loginUser = createAsyncThunk(
  "auth/login",
  async (payload: LoginPayload, { rejectWithValue }) => {
    try {
      const data = await authService.login(payload);
      return mapLoginResponse(data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const requestLoginOtpUser = createAsyncThunk(
  "auth/requestLoginOtp",
  async (payload: RequestLoginOtpPayload, { rejectWithValue }) => {
    try {
      return await authService.requestLoginOtp(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const googleLoginUser = createAsyncThunk(
  "auth/googleLogin",
  async (payload: GoogleAuthPayload, { rejectWithValue }) => {
    try {
      const data = await authService.googleLogin(payload);
      return mapLoginResponse({ ...data, emailVerified: true });
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const appleLoginUser = createAsyncThunk(
  "auth/appleLogin",
  async (payload: AppleAuthPayload, { rejectWithValue }) => {
    try {
      const data = await authService.appleLogin(payload);
      return mapLoginResponse({ ...data, emailVerified: true });
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const verifyOtpUser = createAsyncThunk(
  "auth/verifyOtp",
  async (payload: VerifyEmailPayload, { rejectWithValue }) => {
    try {
      const data = await authService.verifyOtp(payload);
      return mapVerifyEmailResponse(data);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const resendOtpUser = createAsyncThunk(
  "auth/resendOtp",
  async (payload: ResendVerificationPayload, { rejectWithValue }) => {
    try {
      return await authService.resendOtp(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const forgotPasswordUser = createAsyncThunk(
  "auth/forgotPassword",
  async (payload: ForgotPasswordPayload, { rejectWithValue }) => {
    try {
      return await authService.forgotPassword(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const resetPasswordUser = createAsyncThunk(
  "auth/resetPassword",
  async (payload: ResetPasswordPayload, { rejectWithValue }) => {
    try {
      return await authService.resetPassword(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const changePasswordUser = createAsyncThunk(
  "auth/changePassword",
  async (payload: ChangePasswordPayload, { getState, rejectWithValue }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const accessToken = state.auth.accessToken;
    if (!accessToken) {
      return rejectWithValue("You must be signed in to update your password.");
    }
    try {
      return await authService.changePassword(payload, accessToken);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as import("@/src/redux/types").RootState;
    const refreshToken = state.auth.refreshToken;
    if (!refreshToken) return true;
    try {
      await authService.logout({ refreshToken });
      return true;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const fetchProfile = createAsyncThunk(
  "auth/fetchProfile",
  async (_, { rejectWithValue }) => {
    try {
      return await authService.getProfile();
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateProfileUser = createAsyncThunk(
  "auth/updateProfile",
  async (payload: UpdateProfilePayload, { getState, rejectWithValue }) => {
    try {
      const data = await authService.updateProfile(payload);
      const prev = (getState() as RootState).auth.user;
      if (!prev) {
        const mapped = mapUserFromAuthData(data, data.emailVerified ?? true);
        if (!mapped) {
          return rejectWithValue("Invalid profile response");
        }
        return mapped;
      }
      const merged = mapUserFromAuthData(
        { ...prev, ...data, email: data.email ?? prev.email },
        data.emailVerified ?? prev.emailVerified,
      );
      if (!merged) {
        return rejectWithValue("Invalid profile response");
      }
      return merged;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const refreshSession = createAsyncThunk(
  "auth/refreshSession",
  async (payload: RefreshTokenPayload, { rejectWithValue }) => {
    try {
      return await authService.refreshToken(payload);
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);
